/**
 * Document Transformer
 *
 * Transforms and validates document data for workflows
 * Extracted from workflow-data.service.ts for better separation of concerns
 */

import type { WorkflowDocument } from '../workflow-types';

/**
 * Raw document shape from the backend — all fields optional because the
 * transformer is responsible for filling in defaults.
 */
type RawDocument = Partial<WorkflowDocument>;

/**
 * Ensure document has all required fields
 * Since we're using backend data directly, just validate and return
 */
export function transformDocument(doc: unknown): WorkflowDocument {
  // Handle edge case where doc might be undefined or null
  if (!doc || typeof doc !== 'object') {
    console.error('Invalid document object:', doc);
    // Return a minimal valid document in backend structure
    return {
      id: 0,
      originalFilename: 'Invalid Document',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'error',
      s3Key: '',
      imageS3Key: null,
      imageMimeType: null,
      mimeType: 'application/octet-stream',
      fileType: 'other',
      extractedData: null,
      isCertifiedCopy: false,
      metadata: null,
      analysisMetadata: null,
      fileSizeKb: null,
      deletedAt: null
    };
  }

  // Document should already be in backend format, just ensure all fields exist
  const d = doc as RawDocument;
  const originalFilename =
    d.originalFilename || `Document-${d.id || 'unknown'}`;
  return {
    id: d.id || 0,
    originalFilename,
    displayName: d.displayName || originalFilename,
    createdAt: d.createdAt || new Date().toISOString(),
    updatedAt: d.updatedAt || d.createdAt || new Date().toISOString(),
    status: d.status || 'available',
    s3Key: d.s3Key || '',
    imageS3Key: d.imageS3Key || null,
    imageMimeType: d.imageMimeType || null,
    mimeType: d.mimeType || 'application/pdf',
    fileType: d.fileType || 'other',
    extractedData: d.extractedData || null,
    isCertifiedCopy: d.isCertifiedCopy || false,
    metadata: d.metadata || null,
    analysisMetadata: d.analysisMetadata || null,
    fileSizeKb: d.fileSizeKb || null,
    deletedAt: d.deletedAt || null
  };
}
